import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Barbell, Fire, Lightning, Medal, Trophy, X } from "@phosphor-icons/react";
import { MilestoneThumb } from "./MilestoneThumb";

const ICON_MAP = { Barbell, Fire, Lightning, Medal, Trophy };

const ACCENTS = {
  workouts: "#007AFF",
  streak: "#FF9500",
  phase: "#D4FF00",
};

export function BadgeCelebration({ badge, onDismiss, shareToken }) {
  useEffect(() => {
    if (!badge) return;
    const t = setTimeout(() => onDismiss?.(), 3800);
    return () => clearTimeout(t);
  }, [badge, onDismiss]);

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          key={badge.badge_code}
          initial={{ opacity: 0, y: -40, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,360px)]"
          data-testid="badge-celebration"
        >
          <CelebrationCard badge={badge} onDismiss={onDismiss} shareToken={shareToken} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CelebrationCard({ badge, onDismiss, shareToken }) {
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const accent = ACCENTS[badge.category] || "#D4FF00";
  const hasThumb = !!badge.thumbnail_path;
  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-[#0F0F0F] border shadow-2xl"
      style={{ borderColor: `${accent}55` }}
    >
      {/* Glow */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: `radial-gradient(circle at 20% 0%, ${accent}60, transparent 60%)` }}
      />
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/40 hover:bg-black/60 text-zinc-400 hover:text-white flex items-center justify-center z-10"
        aria-label="Chiudi"
        data-testid="badge-celebration-close"
      >
        <X size={11} weight="bold" />
      </button>

      <div className="relative flex items-center gap-3 p-4">
        <motion.div
          initial={{ rotate: -20, scale: 0.4 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.08 }}
          className={`w-14 h-14 flex items-center justify-center flex-shrink-0 relative ${
            hasThumb ? "bg-transparent" : "rounded-2xl"
          }`}
          style={
            hasThumb
              ? { filter: `drop-shadow(0 0 18px ${accent}66)` }
              : {
                backgroundColor: `${accent}22`,
                border: `1px solid ${accent}66`,
                boxShadow: `0 0 28px ${accent}33`,
              }
          }
        >
          {hasThumb ? (
            <MilestoneThumb
              path={badge.thumbnail_path}
              shareToken={shareToken}
              alt={badge.name}
              className="max-w-full max-h-full w-full h-full object-contain object-center"
            />
          ) : (
            <Icon size={28} weight="fill" style={{ color: accent }} />
          )}
          {/* Sparkles */}
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="absolute block w-1 h-1 rounded-full"
              style={{ backgroundColor: accent }}
              initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1.4, 0],
                x: [0, (i - 1) * 18],
                y: [0, -22 - i * 4],
              }}
              transition={{ duration: 1.1, delay: 0.25 + i * 0.12 }}
            />
          ))}
        </motion.div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: accent }}>
            Badge sbloccato
          </p>
          <p className="text-base font-bold text-white leading-tight mt-0.5 truncate">{badge.name}</p>
          {badge.description && (
            <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{badge.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
